'use client'

import clsx from 'clsx'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'

import S from './TasksFeedNav.module.scss'

type FeedPopoverProps = {
  trigger: (props: {
    open: boolean
    toggle: () => void
    menuId: string
  }) => ReactNode
  children: (close: () => void) => ReactNode
  className?: string
  menuClassName?: string
}

/** A chip that opens a menu under it; closes on outside press or Escape. */
export function FeedPopover({
  trigger,
  children,
  className,
  menuClassName
}: FeedPopoverProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const menuId = useId()

  useEffect(() => {
    if (!open) {
      return
    }
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown, true)
    document.addEventListener('touchstart', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true)
      document.removeEventListener('touchstart', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className={clsx(S.popover, className)}>
      {trigger({
        open,
        toggle: () => {
          setOpen(prev => !prev)
        },
        menuId
      })}
      {open ? (
        <div id={menuId} className={clsx(S.menu, menuClassName)}>
          {children(() => {
            setOpen(false)
          })}
        </div>
      ) : null}
    </div>
  )
}
